import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Target,
  Trophy,
  Handshake,
  Megaphone,
  Building2,
  Smartphone,
  ChevronRight,
} from "lucide-react";

const services = [
  {
    id: "strategic",
    icon: Target,
    title: "Strategic Consulting",
    description:
      "Data-driven business strategies tailored to the Cayman Islands market. We help organizations optimize operations, identify growth opportunities, and achieve sustainable success.",
    features: ["Market Analysis", "Growth Strategy", "Operational Excellence"],
  },
  {
    id: "sports",
    icon: Trophy,
    title: "Sports & Event Program Development",
    description:
      "End-to-end sports program and event management expertise. From league development to major event execution, we bring world-class standards to Caribbean sports.",
    features: ["League Development", "Event Management", "Athlete Programs"],
  },
  {
    id: "sponsorship",
    icon: Handshake,
    title: "Sponsorship Proposals",
    description:
      "Compelling sponsorship packages that deliver ROI. We connect brands with the right opportunities and create partnerships that drive mutual value.",
    features: ["Partnership Strategy", "Proposal Development", "ROI Analysis"],
  },
  {
    id: "brand",
    icon: Megaphone,
    title: "Brand Positioning & Marketing",
    description:
      "Strategic brand development that resonates in competitive markets. Build recognition, trust, and loyalty with targeted marketing initiatives.",
    features: ["Brand Strategy", "Marketing Campaigns", "Digital Presence"],
  },
  {
    id: "governance",
    icon: Building2,
    title: "Governance & Structure Development",
    description:
      "Build robust organizational frameworks that ensure accountability, compliance, and long-term sustainability for sports bodies and businesses.",
    features: ["Policy Development", "Compliance", "Board Advisory"],
  },
  {
    id: "app",
    icon: Smartphone,
    title: "App Development",
    description:
      "Custom mobile and web applications tailored to your business needs. From concept to deployment, we build digital solutions that drive engagement and efficiency.",
    features: ["Mobile Apps", "Web Platforms", "Custom Software"],
  },
];

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
    },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.5 },
  },
};

export const ServicesSection = ({ showAll = false }) => {
  const displayServices = showAll ? services : services.slice(0, 3);

  return (
    <section
      data-testid="services-section"
      className="section-padding bg-[#020617] relative"
    >
      {/* Background Pattern */}
      <div className="absolute inset-0 noise-overlay pointer-events-none" />

      <div className="section-container relative z-10">
        {/* Section Header */}
        <div className="mb-16">
          <span className="label-accent mb-4 block">What We Do</span>
          <h2 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold uppercase tracking-tight text-white">
            Our Services
          </h2>
          <p className="text-slate-400 mt-4 max-w-2xl text-lg">
            Comprehensive consulting solutions designed to elevate your business
            and sports endeavors to new heights.
          </p>
        </div>

        {/* Services Grid - Bento Style */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
        >
          {displayServices.map((service, index) => (
            <motion.div
              key={service.id}
              variants={itemVariants}
              id={service.id}
              data-testid={`service-card-${service.id}`}
              className={`card-service group ${
                index === 0 && showAll ? "md:col-span-2 lg:col-span-1" : ""
              }`}
            >
              {/* Icon */}
              <div className="w-14 h-14 rounded-sm bg-[#E53935]/10 flex items-center justify-center mb-6 group-hover:bg-[#E53935]/20 transition-colors">
                <service.icon className="w-7 h-7 text-[#E53935]" />
              </div>

              {/* Content */}
              <h3 className="font-heading text-2xl font-bold uppercase tracking-tight text-white mb-4">
                {service.title}
              </h3>
              <p className="text-slate-400 leading-relaxed mb-6">
                {service.description}
              </p>

              {/* Features */}
              <div className="flex flex-wrap gap-2 mb-6">
                {service.features.map((feature) => (
                  <span
                    key={feature}
                    className="text-xs font-medium tracking-wider uppercase bg-[#1E293B] text-slate-300 px-3 py-1"
                  >
                    {feature}
                  </span>
                ))}
              </div>

              {/* Link */}
              <Link
                to={`/services#${service.id}`}
                className="inline-flex items-center gap-2 text-[#E53935] font-medium text-sm uppercase tracking-wider group-hover:gap-3 transition-all"
              >
                Learn More
                <ChevronRight className="w-4 h-4" />
              </Link>
            </motion.div>
          ))}
        </motion.div>

        {/* View All CTA */}
        {!showAll && (
          <div className="mt-12 text-center">
            <Link
              to="/services"
              data-testid="view-all-services-btn"
              className="btn-secondary inline-flex items-center gap-2"
            >
              View All Services
              <ChevronRight className="w-5 h-5" />
            </Link>
          </div>
        )}
      </div>
    </section>
  );
};

export default ServicesSection;
